import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { bootstrapCaService } from '../services/api';

const BootstrapCAList = () => {
  const [cas, setCas] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, totalCount: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ status: '' });
  const [sorting, setSorting] = useState({ sortBy: 'created_at', sortOrder: 'desc' });
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createMode, setCreateMode] = useState('generate'); // 'generate' or 'upload'
  const [newCa, setNewCa] = useState({
    name: '',
    fingerprint: '',
    certificatePem: '',
    validFrom: '',
    validTo: '',
    description: '',
    validityDays: 365,
  });
  const [creating, setCreating] = useState(false);
  const [generatedCert, setGeneratedCert] = useState(null); // Store generated cert/key for download

  const fetchCas = useCallback(async () => {
    try {
      setLoading(true);
      const [casResponse, statsResponse] = await Promise.all([
        bootstrapCaService.getAll({
          page: pagination.page,
          pageSize: pagination.pageSize,
          sortBy: sorting.sortBy,
          sortOrder: sorting.sortOrder,
          status: filters.status,
        }),
        bootstrapCaService.getStats(),
      ]);
      
      setCas(casResponse.data || []);
      setStats(statsResponse.data || null);
      if (casResponse.pagination) {
        setPagination(prev => ({ ...prev, ...casResponse.pagination }));
      }
    } catch (err) {
      console.error('Error fetching Bootstrap CAs:', err);
      setError('Failed to load Bootstrap CAs');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters, sorting]);

  useEffect(() => {
    fetchCas();
  }, [fetchCas]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => {
        setMessage(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message, error]);

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
    setPagination({ ...pagination, page: 1 });
  };

  const handleSort = (field) => {
    setSorting(prev => ({
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handlePageChange = (newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  const handleCreateCa = async (e) => {
    e.preventDefault();
    try {
      setCreating(true);
      
      const payload = createMode === 'generate' 
        ? {
            name: newCa.name,
            description: newCa.description,
            generateCertificate: true,
            validityDays: parseInt(newCa.validityDays) || 365,
          }
        : {
            name: newCa.name,
            description: newCa.description,
            certificatePem: newCa.certificatePem,
            fingerprint: newCa.fingerprint || undefined,
            validFrom: newCa.validFrom || undefined,
            validTo: newCa.validTo || undefined,
          };
      
      const response = await bootstrapCaService.create(payload);
      
      // If certificate was generated, show it for download
      if (createMode === 'generate' && response.data?.privateKeyPem) {
        setGeneratedCert({
          name: newCa.name,
          certificatePem: response.data.certificatePem,
          privateKeyPem: response.data.privateKeyPem,
          fingerprint: response.data.fingerprint,
          validFrom: response.data.validFrom,
          validTo: response.data.validTo,
        });
        setMessage('Bootstrap CA created! Download the private key now - it will not be shown again.');
      } else {
        setMessage('Bootstrap CA created successfully');
        setShowCreateModal(false);
      }
      
      setNewCa({ name: '', fingerprint: '', certificatePem: '', validFrom: '', validTo: '', description: '', validityDays: 365 });
      fetchCas();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create Bootstrap CA');
    } finally {
      setCreating(false);
    }
  };
  
  const downloadFile = (content, filename) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  const handleDownloadGeneratedCert = () => {
    if (generatedCert) {
      // Download private key
      downloadFile(generatedCert.privateKeyPem, `${generatedCert.name.replace(/\s+/g, '_')}_private_key.pem`);
      // Download certificate
      setTimeout(() => {
        downloadFile(generatedCert.certificatePem, `${generatedCert.name.replace(/\s+/g, '_')}_certificate.pem`);
      }, 500);
    }
  };
  
  const closeGeneratedCertModal = () => {
    setGeneratedCert(null);
    setShowCreateModal(false);
  };

  const handleToggleActive = async (ca) => {
    try {
      if (ca.isActive) {
        await bootstrapCaService.deactivate(ca.id, 'Deactivated via admin UI');
        setMessage(`Bootstrap CA "${ca.name}" deactivated`);
      } else {
        await bootstrapCaService.activate(ca.id);
        setMessage(`Bootstrap CA "${ca.name}" activated`);
      }
      fetchCas();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update Bootstrap CA');
    }
  };

  const getStatusBadge = (ca) => {
    if (!ca.isActive) {
      return <span className="badge badge-gray">⚫ Inactive</span>;
    }
    if (ca.isCurrentlyValid) {
      return <span className="badge badge-green">🟢 Valid</span>;
    }
    return <span className="badge badge-yellow">🟡 Expired</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>🔐 Bootstrap CAs</h1>
        <p className="page-description">
          Manage trusted Bootstrap Certificate Authorities for device registration
        </p>
      </div>

      {/* Messages */}
      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Stats Cards */}
      {stats && (
        <div className="stats-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <div className="stat-card" style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#333' }}>{stats.total}</div>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>Total CAs</div>
          </div>
          <div className="stat-card" style={{ background: '#d4edda', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#155724' }}>{stats.currentlyValid}</div>
            <div style={{ fontSize: '0.875rem', color: '#155724' }}>Currently Valid</div>
          </div>
          <div className="stat-card" style={{ background: '#fff3cd', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#856404' }}>{stats.expired}</div>
            <div style={{ fontSize: '0.875rem', color: '#856404' }}>Expired</div>
          </div>
          <div className="stat-card" style={{ background: '#e2e3e5', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#383d41' }}>{stats.inactive}</div>
            <div style={{ fontSize: '0.875rem', color: '#383d41' }}>Inactive</div>
          </div>
          <div className="stat-card" style={{ background: '#cce5ff', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#004085' }}>{stats.totalUsage}</div>
            <div style={{ fontSize: '0.875rem', color: '#004085' }}>Total Auth Uses</div>
          </div>
        </div>
      )}

      {/* Filters & Actions */}
      <div className="filters-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div className="filters-group">
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="filter-select"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn btn-primary">
          ➕ Add Bootstrap CA
        </button>
      </div>

      {/* CA List */}
      {loading ? (
        <div className="loading">Loading Bootstrap CAs...</div>
      ) : cas.length === 0 ? (
        <div className="empty-state">
          <p>No Bootstrap CAs found</p>
          <p>Click "Add Bootstrap CA" to create one</p>
        </div>
      ) : (
        <>
          <div className="table-container" style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                    Name {sorting.sortBy === 'name' && (sorting.sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Status</th>
                  <th onClick={() => handleSort('valid_from')} style={{ cursor: 'pointer' }}>
                    Valid From {sorting.sortBy === 'valid_from' && (sorting.sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('valid_to')} style={{ cursor: 'pointer' }}>
                    Valid To {sorting.sortBy === 'valid_to' && (sorting.sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('usage_count')} style={{ cursor: 'pointer' }}>
                    Usage {sorting.sortBy === 'usage_count' && (sorting.sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('last_used_at')} style={{ cursor: 'pointer' }}>
                    Last Used {sorting.sortBy === 'last_used_at' && (sorting.sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {cas.map(ca => (
                  <tr key={ca.id}>
                    <td>
                      <Link to={`/bootstrap-cas/${ca.id}`} style={{ fontWeight: 'bold' }}>
                        🔐 {ca.name}
                      </Link>
                      {ca.description && (
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>{ca.description}</div>
                      )}
                    </td>
                    <td>{getStatusBadge(ca)}</td>
                    <td>{formatDate(ca.validFrom)}</td>
                    <td>{formatDate(ca.validTo)}</td>
                    <td>{ca.usageCount}</td>
                    <td>{formatDateTime(ca.lastUsedAt)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <Link to={`/bootstrap-cas/${ca.id}`} className="btn btn-sm btn-secondary">
                          View
                        </Link>
                        <button 
                          onClick={() => handleToggleActive(ca)} 
                          className={`btn btn-sm ${ca.isActive ? 'btn-warning' : 'btn-success'}`}
                        >
                          {ca.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="pagination-btn"
            >
              ← Previous
            </button>
            <span className="pagination-info">
              Page {pagination.page} of {pagination.totalPages} 
              ({pagination.totalCount} total CAs)
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              className="pagination-btn"
            >
              Next →
            </button>
          </div>
        </>
      )}

      {/* Generated Certificate Modal - Shows after auto-generation */}
      {generatedCert && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header" style={{ background: '#28a745', color: 'white' }}>
              <h2>🔐 Certificate Generated Successfully!</h2>
            </div>
            <div className="modal-body">
              <div style={{ background: '#fff3cd', padding: '1rem', borderRadius: '4px', marginBottom: '1rem', border: '1px solid #ffc107' }}>
                <strong>⚠️ IMPORTANT:</strong> Download the private key NOW. It will not be stored or shown again!
              </div>
              
              <div className="form-group">
                <label>CA Name</label>
                <input type="text" value={generatedCert.name} readOnly style={{ background: '#f5f5f5' }} />
              </div>
              
              <div className="form-group">
                <label>Fingerprint (SHA-256)</label>
                <input type="text" value={generatedCert.fingerprint} readOnly style={{ background: '#f5f5f5', fontFamily: 'monospace', fontSize: '0.8rem' }} />
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label>Valid From</label>
                  <input type="text" value={new Date(generatedCert.validFrom).toLocaleString()} readOnly style={{ background: '#f5f5f5' }} />
                </div>
                <div className="form-group">
                  <label>Valid To</label>
                  <input type="text" value={new Date(generatedCert.validTo).toLocaleString()} readOnly style={{ background: '#f5f5f5' }} />
                </div>
              </div>
              
              <div className="form-group">
                <label>Certificate PEM</label>
                <textarea 
                  value={generatedCert.certificatePem} 
                  readOnly 
                  rows={6} 
                  style={{ fontFamily: 'monospace', fontSize: '0.7rem', background: '#f5f5f5' }} 
                />
              </div>
              
              <div className="form-group">
                <label style={{ color: '#dc3545' }}>🔑 Private Key PEM (SAVE THIS!)</label>
                <textarea 
                  value={generatedCert.privateKeyPem} 
                  readOnly 
                  rows={6} 
                  style={{ fontFamily: 'monospace', fontSize: '0.7rem', background: '#fff3cd', border: '2px solid #ffc107' }} 
                />
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleDownloadGeneratedCert} className="btn btn-success" style={{ marginRight: '0.5rem' }}>
                📥 Download Both Files
              </button>
              <button onClick={() => downloadFile(generatedCert.privateKeyPem, `${generatedCert.name.replace(/\s+/g, '_')}_private_key.pem`)} className="btn btn-warning">
                🔑 Download Private Key Only
              </button>
              <button onClick={closeGeneratedCertModal} className="btn btn-secondary" style={{ marginLeft: '1rem' }}>
                Close (I've saved the key)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && !generatedCert && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '650px' }}>
            <div className="modal-header">
              <h2>Add Bootstrap CA</h2>
              <button onClick={() => setShowCreateModal(false)} className="modal-close">×</button>
            </div>
            <form onSubmit={handleCreateCa}>
              <div className="modal-body">
                {/* Mode Selection */}
                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                  <label style={{ marginBottom: '0.5rem', display: 'block' }}>Certificate Source</label>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.75rem 1rem', border: createMode === 'generate' ? '2px solid #007bff' : '1px solid #ddd', borderRadius: '4px', background: createMode === 'generate' ? '#e7f1ff' : 'white' }}>
                      <input
                        type="radio"
                        name="createMode"
                        value="generate"
                        checked={createMode === 'generate'}
                        onChange={() => setCreateMode('generate')}
                      />
                      <span>
                        <strong>🔄 Generate New</strong>
                        <br />
                        <small style={{ color: '#666' }}>Auto-create certificate & key</small>
                      </span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', padding: '0.75rem 1rem', border: createMode === 'upload' ? '2px solid #007bff' : '1px solid #ddd', borderRadius: '4px', background: createMode === 'upload' ? '#e7f1ff' : 'white' }}>
                      <input
                        type="radio"
                        name="createMode"
                        value="upload"
                        checked={createMode === 'upload'}
                        onChange={() => setCreateMode('upload')}
                      />
                      <span>
                        <strong>📤 Upload Existing</strong>
                        <br />
                        <small style={{ color: '#666' }}>Use your own certificate</small>
                      </span>
                    </label>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={newCa.name}
                    onChange={(e) => setNewCa({ ...newCa, name: e.target.value })}
                    placeholder="e.g., Factory-Chennai-2026Q1"
                    required
                  />
                </div>
                
                {/* Generate Mode Fields */}
                {createMode === 'generate' && (
                  <div className="form-group">
                    <label>Validity Period (days)</label>
                    <input
                      type="number"
                      value={newCa.validityDays}
                      onChange={(e) => setNewCa({ ...newCa, validityDays: e.target.value })}
                      min="1"
                      max="3650"
                      placeholder="365"
                    />
                    <small style={{ color: '#666' }}>How long the certificate should be valid (default: 1 year)</small>
                  </div>
                )}
                
                {/* Upload Mode Fields */}
                {createMode === 'upload' && (
                  <>
                    <div className="form-group">
                      <label>SHA-256 Fingerprint <span style={{ color: '#888', fontWeight: 'normal' }}>(auto-generated if empty)</span></label>
                      <input
                        type="text"
                        value={newCa.fingerprint}
                        onChange={(e) => setNewCa({ ...newCa, fingerprint: e.target.value })}
                        placeholder="Leave empty to auto-generate from certificate"
                      />
                      <small style={{ color: '#666' }}>Will be computed automatically from the certificate PEM</small>
                    </div>
                    <div className="form-group">
                      <label>Certificate PEM *</label>
                      <textarea
                        value={newCa.certificatePem}
                        onChange={(e) => setNewCa({ ...newCa, certificatePem: e.target.value })}
                        placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                        rows={8}
                        required
                        style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                      />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div className="form-group">
                        <label>Valid From <span style={{ color: '#888', fontWeight: 'normal' }}>(optional)</span></label>
                        <input
                          type="datetime-local"
                          value={newCa.validFrom}
                          onChange={(e) => setNewCa({ ...newCa, validFrom: e.target.value })}
                        />
                        <small style={{ color: '#666' }}>Defaults to now if empty</small>
                      </div>
                      <div className="form-group">
                        <label>Valid To <span style={{ color: '#888', fontWeight: 'normal' }}>(optional)</span></label>
                        <input
                          type="datetime-local"
                          value={newCa.validTo}
                          onChange={(e) => setNewCa({ ...newCa, validTo: e.target.value })}
                        />
                        <small style={{ color: '#666' }}>Defaults to 1 year if empty</small>
                      </div>
                    </div>
                  </>
                )}
                
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={newCa.description}
                    onChange={(e) => setNewCa({ ...newCa, description: e.target.value })}
                    placeholder="Optional notes about this CA"
                    rows={2}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={creating} className="btn btn-primary">
                  {creating ? 'Creating...' : createMode === 'generate' ? '🔄 Generate & Create CA' : 'Create CA'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BootstrapCAList;
